import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { useAIWorkflow } from "../contexts/AIWorkflowContext";
import { useAuth } from "../contexts/AuthContext";
import jobService from "../services/jobService";
import { generatePdfBlob } from "../components/dashboard/ai_flow/ResumePdfGenerator";
import { templates } from "../components/dashboard/ai_flow/resumeTemplateStyles";
import ExecutivePremiumTemplate from "../components/dashboard/ai_flow/PremiumResume/ExecutivePremiumTemplate";
import StaticExecutivePremiumTemplate from "../components/dashboard/ai_flow/PremiumResume/StaticExecutivePremiumTemplate";

// For the CSS import (for PDF generation)
import executivePremiumCss from "../components/dashboard/ai_flow/PremiumResume/ExecutivePremiumTemplate.css?raw";

// Import all required components
import PdfEditorComponent from "../components/dashboard/ai_flow/PdfEditorComponent";

// A helper to map component names to the STATIC components for PDF export.
const premiumTemplateComponentsForExport = {
  "executive-premium-tae-2": StaticExecutivePremiumTemplate,
};

// A helper to convert structured data to plain text for .txt export or job matching
const convertStructuredToText = (data) => {
  if (!data) return "";
  let text = "";

  const htmlToText = (htmlString) => {
    if (!htmlString) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  if (data.header) {
    text += `${htmlToText(data.header.name)}\n${htmlToText(
      data.header.subtitle
    )}\n${htmlToText(data.header.tagline)}\n\n`;
    if (data.header.contactInfo) {
      text += "CONTACT\n";
      text +=
        data.header.contactInfo.map((item) => item.value).join(" | ") + "\n\n";
    }
  }

  data.mainContent?.forEach((section) => {
    text += `${section.title.toUpperCase()}\n\n`;
    if (section.type === "profile") {
      text += `${htmlToText(section.content)}\n\n`;
    } else if (section.type === "experience") {
      section.items.forEach((job) => {
        text += `${htmlToText(job.jobTitle)}\n`;
        text += `${htmlToText(job.companyInfo)}\n`;
        text += `${htmlToText(job.details)}\n\n`;
      });
    }
  });

  data.sidebarContent?.forEach((section) => {
    text += `${section.title.toUpperCase()}\n\n`;
    if (section.type === "education") {
      section.items.forEach((edu) => {
        text += `${htmlToText(edu.degree)}, ${htmlToText(
          edu.institution
        )} (${htmlToText(edu.period)})\n`;
      });
    } else {
      section.items.forEach((item) => {
        const itemText = item.name ? `${item.name} - ${item.period}` : item;
        text += `- ${itemText}\n`;
      });
    }
    text += "\n";
  });
  return text;
};

// A helper to generate simplified, single-column HTML for MS Word export
const generateSimpleHtmlForWord = (data) => {
  if (!data) return "";

  const { header, mainContent, sidebarContent } = data;

  const styles = `
    body { font-family: Calibri, 'Helvetica Neue', Arial, sans-serif; color: #333; }
    h1 { font-size: 24pt; font-weight: bold; color: #2c3e50; margin-bottom: 5px; }
    h2 { font-size: 11pt; font-weight: normal; text-transform: uppercase; letter-spacing: 3px; color: #34495e; margin-top: 0; margin-bottom: 10px; }
    h3 { font-size: 12pt; font-weight: bold; color: #34495e; margin-top: 20px; margin-bottom: 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    h4 { font-size: 11pt; font-weight: bold; margin-bottom: 2px; color: #2c3e50; }
    p, li { font-size: 10pt; line-height: 1.5; margin-bottom: 4px; }
    ul { padding-left: 20px; }
    a { color: #0000EE; text-decoration: underline; }
    .company-info { color: #555; }
    .institution-info { font-style: italic; color: #666; }
    .tagline { font-style: italic; color: #7f8c8d; }
  `;

  const unwrap = (htmlString) =>
    (htmlString || "").replace(/^<p>/, "").replace(/<\/p>$/, "");
  let body = "";

  if (header) {
    body += `<h1>${unwrap(header.name)}</h1>`;
    body += `<h2>${unwrap(header.subtitle)}</h2>`;
    body += `<p class="tagline">${unwrap(header.tagline)}</p>`;
    if (header.contactInfo) {
      body += `<p>${header.contactInfo.map((c) => c.value).join(" | ")}</p>`;
    }
    body += "<hr />";
  }

  mainContent.forEach((section) => {
    body += `<h3>${section.title}</h3>`;
    if (section.type === "profile") {
      body += section.content;
    } else if (section.type === "experience") {
      section.items.forEach((job) => {
        body += `<h4>${unwrap(job.jobTitle)}</h4>`;
        body += `<p class="company-info">${unwrap(job.companyInfo)}</p>`;
        body += job.details;
      });
    }
  });

  sidebarContent.forEach((section) => {
    body += `<h3>${section.title}</h3>`;
    if (section.type === "education") {
      section.items.forEach((edu) => {
        body += `<h4>${unwrap(edu.degree)}</h4>`;
        body += `<p class="institution-info">${unwrap(
          edu.institution
        )} | ${unwrap(edu.period)}</p>`;
        body += edu.details || "";
      });
    } else if (
      section.type === "competencies" ||
      section.type === "certifications"
    ) {
      const listItems = section.items
        .map(
          (item) =>
            `<li>${item.name ? `${item.name} - ${item.period}` : item}</li>`
        )
        .join("");
      body += `<ul>${listItems}</ul>`;
    }
  });

  return `<html><head><meta charset="UTF-8"><style>${styles}</style></head><body>${body}</body></html>`;
};

function ResumeBuilderPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const {
    masterResume,
    originalSourceInfo,
    isLoadingMasterResume,
    masterResumeError,
    aiSessionId,
    setAiSessionId,
    setAiTaskId,
    userLocationInput,
    pollForAiJobs,
    setResumeSourceForOptimization,
    setFinalResumeContentForJobSearch,
    incrementResumeListVersion,
    generateMasterResumeAction,
    structuredResumeData,
    isLoadingPremiumResume,
    premiumResumeError,
    setStructuredResumeData,
    generatePremiumResumeAction,
  } = useAIWorkflow();

  const basicTemplates = templates.filter((t) => t.type === "basic");
  const premiumTemplates = templates.filter((t) => t.type === "premium");
  const [activeTemplateType, setActiveTemplateType] = useState("basic");
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    basicTemplates[0]?.id || templates[0].id
  );
  const selectedTemplate =
    templates.find((t) => t.id === selectedTemplateId) || templates[0];

  const [editedResume, setEditedResume] = useState("");
  const [pagesData, setPagesData] = useState(null);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [actionStatus, setActionStatus] = useState({
    state: "idle",
    message: "",
  });
  const [isFetchingProceedContent, setIsFetchingProceedContent] =
    useState(false);
  const [isTemplateSelectorCollapsed, setIsTemplateSelectorCollapsed] =
    useState(false);

  const basicDropdownRef = useRef(null);
  const premiumDropdownRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!aiSessionId) return;
    if (
      selectedTemplate.type === "basic" &&
      !masterResume &&
      !isLoadingMasterResume &&
      !masterResumeError
    ) {
      generateMasterResumeAction();
    } else if (
      selectedTemplate.type === "premium" &&
      !structuredResumeData &&
      !isLoadingPremiumResume &&
      !premiumResumeError
    ) {
      generatePremiumResumeAction();
    }
  }, [
    selectedTemplate.type,
    aiSessionId,
    masterResume,
    structuredResumeData,
    isLoadingMasterResume,
    isLoadingPremiumResume,
    masterResumeError,
    premiumResumeError,
    generateMasterResumeAction,
    generatePremiumResumeAction,
  ]);

  useEffect(() => {
    if (
      selectedTemplate.type === "basic" &&
      masterResume &&
      !masterResumeError
    ) {
      setEditedResume((prev) =>
        prev === "" || prev === masterResume ? masterResume : prev
      );
    }
  }, [masterResume, masterResumeError, selectedTemplate.type]);

  const handleEditorContentChange = useCallback((newContent) => {
    setEditedResume(newContent);
  }, []);

  const handlePagesDataChange = useCallback((pages) => {
    setPagesData(pages);
  }, []);

  const handleTemplateTypeChange = (type) => {
    setActiveTemplateType(type);
    if (type === "basic" && basicTemplates.length > 0) {
      setSelectedTemplateId(basicTemplates[0].id);
    } else if (type === "premium" && premiumTemplates.length > 0) {
      // Look for the minimalist template
      const minimalistTemplate = premiumTemplates.find(
        (t) =>
          t.id === "minimalist" || t.name.toLowerCase().includes("minimalist")
      );

      if (minimalistTemplate) {
        setSelectedTemplateId(minimalistTemplate.id);
      } else {
        // Fallback to first premium template if minimalist not found
        setSelectedTemplateId(premiumTemplates[0].id);
      }
    }
    // Auto-collapse after selection
    setIsTemplateSelectorCollapsed(true);
  };

  const handleTemplateSelect = (event) => {
    setSelectedTemplateId(event.target.value);
    // Auto-collapse after selection
    setIsTemplateSelectorCollapsed(true);
  };

  const getStyledHtmlForExport = (content) => {
    const template =
      templates.find((t) => t.id === selectedTemplateId) || templates[0];
    return `<html><head><meta charset="UTF-8"><style>${template.styles}</style></head><body><div class="resume-template-container template-${template.id}">${content}</div></body></html>`;
  };

  const genericDownload = (blob, filename) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const beginAction = (actionName) => {
    setIsActionInProgress(true);
    setActiveAction(actionName);
    setActionStatus({ state: "processing", message: "Please wait..." });
  };

  const endAction = (state = "idle", message = "") => {
    if (state !== "processing") {
      setTimeout(
        () =>
          setActionStatus((prev) =>
            prev.message === message ? { state: "idle", message: "" } : prev
          ),
        3000
      );
    }
    setActionStatus({ state, message });
    setIsActionInProgress(false);
    setActiveAction(null);
  };

  const performPdfAction = async (actionType) => {
    const suggestedName = originalSourceInfo?.title || "Generated Resume";
    const promptMessage =
      actionType === "save"
        ? "Enter a name to save this PDF resume as:"
        : "Enter a filename for the download:";
    const finalName = window.prompt(promptMessage, suggestedName);
    if (!finalName || !finalName.trim()) return;

    beginAction(actionType);

    try {
      setActionStatus({ state: "processing", message: "Generating PDF..." });

      let pdfBlob;

      if (selectedTemplate.type === "premium") {
        if (!structuredResumeData) {
          throw new Error("Premium resume data is not available.");
        }

        pdfBlob = await generatePdfBlob(
          null,
          selectedTemplate.id,
          structuredResumeData,
          null
        );
      } else {
        const content = editedResume || masterResume;
        if (!content || content.replace(/<[^>]*>/g, "").trim().length < 30) {
          throw new Error("Cannot process empty or very short content.");
        }

        pdfBlob = await generatePdfBlob(
          content,
          selectedTemplate.id,
          null,
          pagesData
        );
      }

      if (actionType === "save") {
        const pdfFile = new File([pdfBlob], `${finalName.trim()}.pdf`, {
          type: "application/pdf",
        });
        const savedResume = await jobService.saveResumeFile(
          pdfFile,
          finalName.trim(),
          false
        );
        incrementResumeListVersion();
        endAction("success", `Resume "${savedResume.title}" saved!`);
      } else {
        genericDownload(pdfBlob, `${finalName.trim()}.pdf`);
        endAction("success", "Download started!");
      }
    } catch (err) {
      console.error("PDF generation error:", err);
      endAction(
        "error",
        err.message || "An error occurred while generating the PDF."
      );
    }
  };

  const handleSavePdf = () => performPdfAction("save");
  const handleDownloadPdf = () => performPdfAction("download_pdf");

  const handlePreviewPdf = async () => {
    try {
      beginAction("preview");
      setActionStatus({
        state: "processing",
        message: "Generating preview...",
      });

      let pdfBlob;

      if (selectedTemplate.type === "premium") {
        if (!structuredResumeData) {
          throw new Error("Premium resume data is not available.");
        }
        pdfBlob = await generatePdfBlob(
          null,
          selectedTemplate.id,
          structuredResumeData,
          null
        );
      } else {
        const content = editedResume || masterResume;
        if (!content || content.replace(/<[^>]*>/g, "").trim().length < 30) {
          throw new Error("Cannot process empty or very short content.");
        }
        pdfBlob = await generatePdfBlob(
          content,
          selectedTemplate.id,
          null,
          pagesData
        );
      }

      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);

      endAction("success", "Preview opened in new tab!");
    } catch (err) {
      console.error("Preview error:", err);
      endAction("error", err.message || "Error generating preview.");
    }
  };

  const performTextDocAction = (fileType) => {
    const suggestedName = originalSourceInfo?.title || "Generated Resume";
    const finalName = window.prompt(
      "Enter a filename for the download:",
      suggestedName
    );
    if (!finalName || !finalName.trim()) return;

    let blob;
    try {
      if (selectedTemplate.type === "premium") {
        if (!structuredResumeData)
          throw new Error("Premium data not available.");

        if (fileType === "txt") {
          const textContent = convertStructuredToText(structuredResumeData);
          blob = new Blob([textContent], { type: "text/plain;charset=utf-8;" });
        } else {
          const wordCompatibleHtml =
            generateSimpleHtmlForWord(structuredResumeData);
          blob = new Blob(["\ufeff", wordCompatibleHtml], {
            type: "application/msword",
          });
        }
      } else {
        const content = editedResume || masterResume;
        if (!content || content.replace(/<[^>]*>/g, "").trim().length < 30) {
          throw new Error("Cannot process empty or very short content.");
        }
        if (fileType === "txt") {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = content;
          const textContent = tempDiv.textContent || tempDiv.innerText || "";
          blob = new Blob([textContent], { type: "text/plain;charset=utf-8;" });
        } else {
          const styledHtml = getStyledHtmlForExport(content);
          blob = new Blob(["\ufeff", styledHtml], {
            type: "application/msword",
          });
        }
      }
      genericDownload(
        blob,
        `${finalName.trim()}.${fileType === "txt" ? "txt" : "doc"}`
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDownloadTxt = () => performTextDocAction("txt");
  const handleDownloadDocx = () => performTextDocAction("doc");

  const handleProceed = async () => {
    let finalContent = null;
    let finalSource = "generated";
    setIsFetchingProceedContent(true);
    setActionStatus({
      state: "processing",
      message: "Preparing to search for jobs...",
    });

    try {
      if (selectedTemplate.type === "premium") {
        if (!structuredResumeData)
          throw new Error("Premium resume data is not available.");
        finalContent = convertStructuredToText(structuredResumeData);
      } else {
        finalContent = editedResume || masterResume;
      }

      if (
        !finalContent ||
        (typeof finalContent === "string" &&
          finalContent.replace(/<[^>]*>/g, "").trim().length < 30)
      ) {
        throw new Error("Editor content is empty or too short.");
      }

      setFinalResumeContentForJobSearch(finalContent);
      setResumeSourceForOptimization(finalSource);

      if (!currentUser?.email) {
        throw new Error(
          "User email not found. Please ensure you're logged in."
        );
      }

      if (!userLocationInput) {
        throw new Error("Location not found. Please start from the beginning.");
      }

      setActionStatus({
        state: "processing",
        message: "Starting job search...",
      });

      const searchResult = await jobService.submitResumeForMatching(
        currentUser.email,
        finalContent,
        userLocationInput
      );

      if (searchResult?.task_id && searchResult?.session_id) {
        if (setAiTaskId) setAiTaskId(searchResult.task_id);
        if (setAiSessionId) setAiSessionId(searchResult.session_id);

        if (pollForAiJobs) {
          pollForAiJobs(searchResult.task_id);
        }

        navigate("/dashboard/job-listing");
      } else {
        throw new Error(
          "Failed to start job search. Invalid response from server."
        );
      }
    } catch (err) {
      console.error("Error during proceed:", err);
      setActionStatus({
        state: "error",
        message: `Error: ${err.message}`,
      });
      setTimeout(
        () =>
          setActionStatus((prev) =>
            prev.message === `Error: ${err.message}`
              ? { state: "idle", message: "" }
              : prev
          ),
        5000
      );
    } finally {
      setIsFetchingProceedContent(false);
    }
  };

  const handleRetryGeneration = () => {
    if (selectedTemplate.type === "premium") {
      generatePremiumResumeAction();
    } else {
      generateMasterResumeAction();
    }
  };

  const isActionDisabled = () => {
    if (isActionInProgress) return true;
    if (selectedTemplate.type === "premium") {
      return !structuredResumeData || isLoadingPremiumResume;
    } else {
      const c = editedResume || masterResume;
      if (isLoadingMasterResume || !c || c === "<p></p>") return true;
      return c.replace(/<[^>]*>/g, "").trim().length < 30;
    }
  };

  const isProceedDisabled = () => {
    if (isFetchingProceedContent) return true;
    return isActionDisabled();
  };

  const renderMainContent = () => {
    if (selectedTemplate.type === "premium") {
      if (isLoadingPremiumResume)
        return (
          <div className="loading-placeholder widget-card p-6 text-center text-gray-600 bg-gray-50 rounded-lg border border-gray-200 animate-pulse">
            Generating Premium Resume...
          </div>
        );
      if (premiumResumeError)
        return (
          <div className="widget-card bg-red-50 border border-red-200 p-3 rounded-lg">
            <p className="text-red-700 font-medium">
              Error: {premiumResumeError}
            </p>
            <button
              onClick={handleRetryGeneration}
              className="smai-button-secondary text-sm mt-2"
            >
              Retry
            </button>
          </div>
        );
      if (structuredResumeData) {
        return (
          <div
            className="premium-wysiwyg-container"
            style={{
              position: "relative",
              width: "100%",
              height: "calc(100vh - 200px)",
              overflow: "hidden",
            }}
          >
            <ExecutivePremiumTemplate
              data={structuredResumeData}
              onUpdateData={setStructuredResumeData}
            />
          </div>
        );
      }
      return (
        <div className="loading-placeholder widget-card p-6 text-center text-gray-600 bg-gray-50">
          Select a premium template to generate structured content.
        </div>
      );
    } else {
      if (isLoadingMasterResume)
        return (
          <div className="loading-placeholder widget-card p-6 text-center text-gray-600 bg-gray-50 rounded-lg border border-gray-200 animate-pulse">
            Generating AI Master Resume...
          </div>
        );
      if (masterResumeError)
        return (
          <div className="widget-card bg-red-50 border border-red-200 p-3 rounded-lg">
            <p className="text-red-700 font-medium">
              Error: {masterResumeError}
            </p>
            <button
              onClick={handleRetryGeneration}
              className="smai-button-secondary text-sm mt-2"
            >
              Retry
            </button>
          </div>
        );
      return (
        <PdfEditorComponent
          initialContent={masterResume || ""}
          isLoading={isLoadingMasterResume}
          onContentChange={handleEditorContentChange}
          onPagesDataChange={handlePagesDataChange}
          templateId={selectedTemplateId}
        />
      );
    }
  };

  // Check if we're using a premium template
  const isPremiumTemplate =
    selectedTemplate.type === "premium" ||
    ["minimalist", "creative", "executive", "executive-premium-tae-2"].includes(
      selectedTemplateId
    );

  return (
    <div className="resume-builder-page md:p-6 lg:p-8 space-y-6 max-w-full mx-auto">
      {!aiSessionId && (
        <div className="warning-message widget-card p-6 text-center text-gray-600 bg-yellow-50 rounded-lg border border-yellow-200">
          AI session not found. Please start the process from the beginning.
        </div>
      )}

      {aiSessionId && (
        <>
          {/* Collapsible Template Selector */}
          <div
            className="template-selector-wrapper"
            style={{ marginBottom: "1rem" }}
          >
            <button
              onClick={() =>
                setIsTemplateSelectorCollapsed(!isTemplateSelectorCollapsed)
              }
              className="template-selector-toggle"
              style={{
                width: "100%",
                padding: "0.75rem",
                backgroundColor: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: "500",
                color: "#374151",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e5e7eb";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <svg
                  className={`w-4 h-4 transform transition-transform ${
                    isTemplateSelectorCollapsed ? "" : "rotate-90"
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Editor Selection
              </span>

              {/* Choose Different Career Path Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent toggle from firing
                  navigate("/dashboard/ai-matchmaking");
                }}
                style={{
                  padding: "0.375rem 0.75rem",
                  backgroundColor: "#ffffff",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  fontSize: "0.75rem",
                  fontWeight: "500",
                  color: "#4b5563",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                title="Go back to Career Path Mapping"
              >
                Choose a Different Career Path
              </button>

              <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                Current:{" "}
                {activeTemplateType === "premium"
                  ? "Premium Editor"
                  : `${selectedTemplate.name} (Basic)`}
              </span>
            </button>
            {!isTemplateSelectorCollapsed && (
              <div
                className="template-selector-content"
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  animation: "slideDown 0.2s ease-out",
                }}
              >
                <div className="template-header-container">
                  <div
                    className={`template-selector-group ${
                      activeTemplateType === "basic" ? "is-active" : ""
                    }`}
                  >
                    <label onClick={() => handleTemplateTypeChange("basic")}>
                      Basic Editor
                    </label>
                    <CSSTransition
                      nodeRef={basicDropdownRef}
                      in={activeTemplateType === "basic"}
                      timeout={300}
                      classNames="dropdown-fluid-fade"
                      unmountOnExit
                    >
                      <select
                        ref={basicDropdownRef}
                        id="basic-template-select"
                        value={selectedTemplateId}
                        onChange={handleTemplateSelect}
                        className="template-dropdown-select"
                      >
                        {basicTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </CSSTransition>
                  </div>
                  <div
                    className={`template-selector-group ${
                      activeTemplateType === "premium" ? "is-active" : ""
                    }`}
                  >
                    <label onClick={() => handleTemplateTypeChange("premium")}>
                      Premium Editor
                    </label>
                    <CSSTransition
                      nodeRef={premiumDropdownRef}
                      in={activeTemplateType === "premium"}
                      timeout={300}
                      classNames="dropdown-fluid-fade"
                      unmountOnExit
                    >
                      <select
                        ref={premiumDropdownRef}
                        id="premium-template-select"
                        value={selectedTemplateId}
                        onChange={handleTemplateSelect}
                        className="template-dropdown-select"
                      >
                        {premiumTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </CSSTransition>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Add CSS for animation */}
          <style jsx>{`
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>

          <div className="widget-card p-0 overflow-hidden relative">
            <TransitionGroup className="content-transition-group">
              <CSSTransition
                nodeRef={contentRef}
                key={selectedTemplate.type}
                timeout={300}
                classNames="content-fade"
              >
                <div className="content-transition-item" ref={contentRef}>
                  {renderMainContent()}
                </div>
              </CSSTransition>
            </TransitionGroup>

            {/* CONDITIONAL RENDERING: Only show export buttons for basic templates */}
            {!isPremiumTemplate && (
              <div className="p-4 border-t bg-gray-50 flex flex-wrap items-center justify-end gap-3">
                {actionStatus.message && (
                  <span
                    className={`text-sm font-medium order-first sm:order-none w-full sm:w-auto text-center sm:text-right ${
                      actionStatus.state === "success"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {actionStatus.message}
                  </span>
                )}
                <button
                  onClick={handleDownloadTxt}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                >
                  Download TXT
                </button>
                <button
                  onClick={handleDownloadDocx}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                >
                  Download DOC
                </button>
                <button
                  onClick={handlePreviewPdf}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                  title="Preview PDF in new tab"
                >
                  Preview PDF
                </button>
                <button
                  onClick={handleDownloadPdf}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                >
                  Download PDF
                </button>
                <button
                  onClick={handleSavePdf}
                  className="opt-page-btn opt-page-btn-primary"
                  disabled={isActionDisabled()}
                >
                  Save to Account
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center flex-wrap items-center p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
            <button
              onClick={handleProceed}
              className="px-6 py-2.5 rounded-md text-sm font-medium text-white bg-emerald-800 hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0 flex items-center justify-center"
              disabled={isProceedDisabled()}
              title="Use editor content to find jobs"
            >
              Proceed with Editor Content & Find Jobs
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ResumeBuilderPage;
